import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { MagnifyingGlass, CalendarCheck, CircleNotch, ArrowRight } from "@phosphor-icons/react";

const STATUS_STYLE = {
  Scheduled: { bg: "var(--bk-rowan-bg)", color: "var(--bk-accent)" },
  Completed: { bg: "var(--bk-success-bg)", color: "var(--bk-success)" },
  Cancelled: { bg: "#F3F1EC", color: "var(--bk-text-2)" },
  "No-show": { bg: "#FFF3DC", color: "#B08840" },
};
const FILTERS = { all: "All", upcoming: "Upcoming", completed: "Completed", missed: "Cancelled & no-shows" };

// Read-only appointment history across the scheduler. `mode="missed"` starts on cancellations & no-shows.
export function BookingHistory({ businessId, mode = "all", onOpenScheduler }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState(mode === "missed" ? "missed" : "upcoming");
  const [q, setQ] = useState("");
  useEffect(() => { api.get(`/businesses/${businessId}/schedules`).then((r) => setRows(r.data || [])).catch(() => setRows([])); }, [businessId]);

  const today = new Date().toISOString().slice(0, 10);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (rows || []).filter((b) => {
      const who = b.client_name || b.booked_by || "";
      if (s && !`${who} ${b.equipment || ""} ${b.label || ""} ${b.service_name || ""}`.toLowerCase().includes(s)) return false;
      if (filter === "upcoming") return b.status === "Scheduled" && (b.date || "") >= today;
      if (filter === "completed") return b.status === "Completed";
      if (filter === "missed") return b.status === "Cancelled" || b.status === "No-show";
      return true;
    }).sort((a, b) => (filter === "upcoming" ? (a.date || "").localeCompare(b.date || "") : (b.date || "").localeCompare(a.date || "")));
  }, [rows, filter, q, today]);

  const missed = (rows || []).filter((b) => b.status === "Cancelled" || b.status === "No-show");
  const noShows = missed.filter((b) => b.status === "No-show");

  return (
    <div className="space-y-4" data-testid="booking-history">
      {mode === "missed" && rows && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="missed-summary">
          {[["Cancelled", missed.length - noShows.length], ["No-shows", noShows.length], ["Sessions deducted", noShows.filter((b) => b.no_show?.deducted || b.session_deducted).length]].map(([k, v]) => (
            <div key={k} className="bk-card p-4"><p className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--bk-text-2)" }}>{k}</p><p className="font-display text-2xl font-bold mt-1" style={{ color: "var(--bk-text)" }}>{v}</p></div>
          ))}
        </div>
      )}
      <div className="bk-card p-4 flex flex-wrap items-center gap-2">
        {Object.entries(FILTERS).map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`booking-filter-${k}`} className="px-3.5 py-1.5 rounded-full text-sm font-semibold" style={filter === k ? { background: "var(--bk-accent)", color: "#fff" } : { border: "1px solid var(--bk-border)", color: "var(--bk-text-2)" }}>{label}</button>
        ))}
        <label className="relative ml-auto">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" color="var(--bk-text-2)" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by client" data-testid="booking-search-input" className="rounded-full border pl-9 pr-4 py-1.5 text-sm outline-none" style={{ borderColor: "var(--bk-border)" }} />
        </label>
        {onOpenScheduler && <button onClick={onOpenScheduler} data-testid="open-scheduler-btn" className="text-sm font-semibold inline-flex items-center gap-1" style={{ color: "var(--bk-accent)" }}>Open scheduler <ArrowRight size={12} weight="bold" /></button>}
      </div>
      {rows === null ? (
        <p className="text-sm inline-flex items-center gap-2" style={{ color: "var(--bk-text-2)" }}><CircleNotch size={16} className="animate-spin" /> Loading bookings…</p>
      ) : list.length === 0 ? (
        <div className="bk-card p-10 text-center" data-testid="booking-history-empty"><CalendarCheck size={36} weight="duotone" color="var(--bk-accent)" className="mx-auto" /><p className="font-semibold mt-3" style={{ color: "var(--bk-text)" }}>Nothing here yet.</p><p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{filter === "missed" ? "No cancellations or no-shows — nice." : "New bookings will show up here."}</p></div>
      ) : (
        <div className="space-y-2" data-testid="booking-history-list">
          {list.map((b, i) => {
            const st = STATUS_STYLE[b.status] || STATUS_STYLE.Scheduled;
            return (
              <div key={b.id} className="bk-card p-4 flex flex-wrap items-center gap-3" data-testid={`booking-row-${i}`}>
                <div className="w-20 shrink-0"><p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{b.date}</p><p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{[b.start_time, b.end_time].filter(Boolean).join("–")}</p></div>
                <div className="flex-1 min-w-[200px]"><p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{b.client_name || b.booked_by || "Walk-in"}</p><p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{[b.service_name, b.equipment, b.label].filter(Boolean).join(" · ") || "Booking"}</p></div>
                {b.status === "No-show" && <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{(b.no_show?.deducted || b.session_deducted) ? "Session deducted" : "No session deducted"}</span>}
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: st.bg, color: st.color }}>{b.status}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
